import {CA} from '../analytics/Analytics'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../shared/entity_datas/EntityDatas'
import {DicePair, GameAction, GameActionClientData, ReportData, UserReportReasonEnum} from '@colonist/game-base-types'
import {AjaxDataTypes} from '../../../../shared/AjaxDataTypes'
import {ClientMessageSubType, ServerId} from '@colonist/types'
import {TradeCreationData, TradeExecutionData, TradeResponse} from '@colonist/game-mechanic-validators'
import {CardEnum, CityImprovementTypes, PlayerColor} from '@colonist/game-states'
import {SocketSender} from './SocketSender'

export class SocketGameSend {
    private readonly sender: SocketSender
    private readonly getServerId: () => ServerId
    private gameActionSendSequence: number = 1

    constructor(sender: SocketSender, getServerId: () => ServerId) {
        this.sender = sender
        this.getServerId = getServerId // find a better way to access the serverId...
    }

    // ====================================================================================================================================
    // Game - Trade
    // ====================================================================================================================================

    createTrade(offer: TradeCreationData) {
        this.send(GameAction.CreateTrade, offer)
    }

    executeTrade(offerId: string, playerColor: PlayerColor) {
        const dataToSend: TradeExecutionData = {tradeId: offerId, playerToExecuteTradeWith: playerColor}
        this.send(GameAction.ExecuteTrade, dataToSend)
    }

    updateTradeResponse(tradeResponse: TradeResponse) {
        this.send(GameAction.UpdateTradeResponse, tradeResponse)
    }

    toggleEmbargoAgainstPlayer(targetPlayer: PlayerColor) {
        this.send(GameAction.ToggledEmbargo, targetPlayer)
    }

    // ====================================================================================================================================
    // Game - General
    // ====================================================================================================================================

    clickedPassTurn() {
        this.send(GameAction.PassedTurn, true)
    }

    clickedDice() {
        this.send(GameAction.ClickedDice, true)
    }

    selectedTile(tileIndex: number) {
        this.send(GameAction.SelectedTile, tileIndex)
    }

    selectedTiles(tileIndexes: number[]) {
        this.send(GameAction.SelectedTiles, tileIndexes)
    }

    selectCards(resourcesToSend: CardEnum[]) {
        this.send(GameAction.SelectedCards, resourcesToSend)
    }

    selectCardsState(resourcesToSend: CardEnum[]) {
        this.send(GameAction.SelectedCardsState, resourcesToSend)
    }

    selectPlayer(playerColor: PlayerColor) {
        this.send(GameAction.SelectedPlayer, playerColor)
    }

    selectedInitialPlacementIndex(placementIndex: number | undefined) {
        this.send(GameAction.SelectedInitialPlacementIndex, placementIndex)
    }

    buyDevCard() {
        this.send(GameAction.BuyDevelopmentCard, true)
    }

    buildRoad() {
        this.send(GameAction.WantToBuildRoad, true)
    }

    buildSettlement() {
        this.send(GameAction.WantToBuildSettlement, true)
    }

    buildCity() {
        this.send(GameAction.WantToBuildCity, true)
    }

    buildShip() {
        this.send(GameAction.WantToBuildShip, true)
    }

    moveShip() {
        this.send(GameAction.WantToMoveShip, true)
    }

    buildCityWall() {
        this.send(GameAction.WantToBuildCityWall, true)
    }

    placeKnight() {
        this.send(GameAction.WantToPlaceKnight, true)
    }

    upgradeKnight() {
        this.send(GameAction.WantToUpgradeKnight, true)
    }

    activateKnight() {
        this.send(GameAction.WantToActivateKnight, true)
    }

    takeKnightAction() {
        this.send(GameAction.WantToTakeKnightAction, true)
    }

    confirmBuildRoad(edgeTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildRoad, edgeTileNo)
    }

    confirmBuildRoadSkippingSelection(edgeTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildRoadSkippingSelection, edgeTileNo)
    }

    confirmBuildSettlement(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildSettlement, cornerTileNo)
    }

    confirmBuildSettlementSkippingSelection(edgeTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildSettlementSkippingSelection, edgeTileNo)
    }

    confirmBuildCity(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildCity, cornerTileNo)
    }

    confirmBuildCitySkippingSelection(edgeTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildCitySkippingSelection, edgeTileNo)
    }

    confirmBuildShip(edgeTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildShip, edgeTileNo)
    }

    confirmBuildShipSkippingSelection(edgeTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildShipSkippingSelection, edgeTileNo)
    }

    selectedShipToMove(edgeTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.SelectedShipToMove, edgeTileNo)
    }

    confirmMoveShip(edgeTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmMoveShip, edgeTileNo)
    }

    confirmRemoveRoad(edgeTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmRemoveRoad, edgeTileNo)
    }

    confirmBuildCityWall(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildCityWall, cornerTileNo)
    }

    confirmBuildCityWallSkippingSelection(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmBuildCityWallSkippingSelection, cornerTileNo)
    }

    confirmPlaceKnight(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmPlaceKnight, cornerTileNo)
    }

    confirmPlaceKnightSkippingSelection(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmPlaceKnightSkippingSelection, cornerTileNo)
    }

    confirmUpgradeKnight(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmUpgradeKnight, cornerTileNo)
    }

    confirmUpgradeKnightSkippingSelection(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmUpgradeKnightSkippingSelection, cornerTileNo)
    }

    confirmActivateKnight(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmActivateKnight, cornerTileNo)
    }

    confirmActivateKnightSkippingSelection(cornerTileNo: number) {
        this.sendConfirmBuildAnalytic()
        this.send(GameAction.ConfirmActivateKnightSkippingSelection, cornerTileNo)
    }

    // eslint-disable-next-line class-methods-use-this
    private sendConfirmBuildAnalytic() {
        // CA.sendGoogleAnalyticsEventOnce(CA.Category.Funnel, CA.Label.FunnelGameConfirmBuild, UIGameEventEnums.GameEnd)
    }

    // eslint-disable-next-line class-methods-use-this
    async sendGameAnalyticOnce(type: AnalyticEventType, value?: number) {
        const eventData: AjaxDataTypes.AnalyticData = {
            type: type,
            category: AnalyticEventCategory.Game,
            floatValue: value,
        }
        await CA.sendAnalyticEventOnce(eventData)
    }

    confirmCityUpgrade(cityImprovementType: CityImprovementTypes) {
        this.send(GameAction.ConfirmCityUpgrade, cityImprovementType)
    }

    confirmPlaceMetropolis(cornerTileNo: number) {
        this.send(GameAction.ConfirmBuildMetropolis, cornerTileNo)
    }

    confirmPillageCity(cornerTileNo: number) {
        this.send(GameAction.ConfirmPillageCity, cornerTileNo)
    }

    confirmSelectKnightToTakeAction(cornerTileNo: number) {
        this.send(GameAction.SelectKnightToTakeAction, cornerTileNo)
    }

    confirmMoveKnight(cornerTileNo: number) {
        this.send(GameAction.ConfirmMoveKnight, cornerTileNo)
    }

    confirmMoveKnightOffTurn(cornerTileNo: number) {
        this.send(GameAction.ConfirmMoveKnightOffTurn, cornerTileNo)
    }

    confirmRemoveKnight(cornerTileNo: number) {
        this.send(GameAction.ConfirmRemoveKnight, cornerTileNo)
    }

    confirmDiceSelection(confirmDice: DicePair) {
        this.send(GameAction.ConfirmDicePair, confirmDice)
    }

    cancelAction() {
        this.send(GameAction.CancelAction, true)
    }

    clickedDevelopmentCard(cardEnum: CardEnum) {
        this.send(GameAction.ClickedDevelopmentCard, cardEnum)
    }

    requestSpecialBuildPhase() {
        this.send(GameAction.RequestSpecialBuildPhase, true)
    }

    cancelSpecialBuildPhase() {
        this.send(GameAction.CancelSpecialBuildPhase, true)
    }

    clickedRematch() {
        this.send(GameAction.ClickedRematch, true)
    }

    clickedVote(id: number, accepted: boolean) {
        this.send(GameAction.Vote, {id: id, accepted: accepted})
    }

    requestActionSwap(requestedAction: GameAction) {
        this.send(GameAction.RequestActionSwap, requestedAction)
    }

    requestHintForBeginner(isUsersFirstGame: boolean) {
        this.send(GameAction.RequestBeginnerHint, isUsersFirstGame)
    }

    skipWelcomeActionOnBeginnerMode() {
        this.send(GameAction.SkipWelcomeAction, true)
    }

    endBeginnerModeLevel() {
        this.send(GameAction.RequestBeginnerModeLevelEnd, true)
    }

    togglePause() {
        this.send(GameAction.TogglePause, true)
    }

    leaveGame() {
        this.send(GameAction.LeaveGame, true)
    }

    resign() {
        this.send(GameAction.Resign, true)
    }

    submitChat(text: string) {
        this.send(GameAction.GameChatSubmit, text)
    }

    reportPlayer(reportReason: UserReportReasonEnum, playerColor: PlayerColor) {
        const dataToSend: ReportData = {reportReason: reportReason, reportedPlayerColor: playerColor}
        this.send(GameAction.ReportPlayer, dataToSend)
    }

    requestGameState() {
        this.send(GameAction.RequestGameState, this.getServerId())
    }

    pong() {
        this.send(GameAction.Pong, undefined)
    }

    notifyBackendClientAutoReconnected() {
        this.send(GameAction.NotifyBackendClientAutoReconnected, undefined)
    }

    private send(action: GameAction, payload: unknown) {
        const data: GameActionClientData = {
            action: action,
            payload: payload,
            sequence: this.gameActionSendSequence += 1,
        }

        this.sender.sendToServer(ClientMessageSubType.GameAction, this.getServerId(), data)
    }
}
